import React from "react";
import type { StepId } from "../../types";
import { AnimatePresence } from "framer-motion";
import {
  FormProvider,
  Controller,
  Control,
  FieldErrors,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { useFormWizard } from "../../hooks/useFormWizard";
import {
  getStepConfig,
  getSubPropertyTypeOptions,
  getStepTitle,
} from "../../config/formSteps";
import ProgressBar from "./ProgressBar";
import NavigationButtons from "./NavigationButtons";
import FormStep from "./FormStep";
import FieldRenderer from "../FormFields/FieldRenderer";
import Input from "../FormFields/Input";
import CheckboxField from "../FormFields/CheckboxField";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import type { PropertyFormData, ContactFormData } from "../../types";
import type { FieldDefinition } from "../../types";

interface FormWizardProps {
  onSuccess: () => void;
  onError?: (error: Error) => void;
  className?: string;
  currentStepId: StepId;
  onStepChange: (stepId: StepId) => void;
}

// Wrapper to sync contact fields with both react-hook-form and contactData state
interface ContactFieldWrapperProps {
  field: FieldDefinition;
  control: Control<any>;
  errors: FieldErrors;
  contactData: ContactFormData;
  setContactData: React.Dispatch<React.SetStateAction<ContactFormData>>;
}

const ContactFieldWrapper: React.FC<ContactFieldWrapperProps> = ({
  field,
  control,
  errors,
  contactData,
  setContactData,
}) => {
  const fieldValue =
    contactData[field.name as keyof ContactFormData] ||
    field.defaultValue ||
    "";

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={fieldValue}
      render={({ field: fieldProps }) => {
        // Create a wrapper that syncs to both states
        const syncedFieldProps = {
          ...fieldProps,
          value: fieldProps.value || fieldValue,
          onChange: (e: any) => {
            const value = e?.target?.value !== undefined ? e.target.value : e;
            fieldProps.onChange(value);
            setContactData((prev) => ({ ...prev, [field.name]: value }));
          },
        };

        // Render based on field type
        const error = errors[field.name]?.message as string | undefined;

        switch (field.type) {
          case "text":
            // Use email type for email field
            const inputType = field.name === "email" ? "email" : "text";
            return (
              <Input
                {...syncedFieldProps}
                variant="normal"
                type={inputType}
                label={field.label}
                placeholder={field.placeholder}
                error={error}
              />
            );
          case "checkbox": {
            return (
              <CheckboxField
                {...syncedFieldProps}
                checked={syncedFieldProps.value || false}
                onChange={(checked) => {
                  syncedFieldProps.onChange(checked);
                }}
                label={field.checkboxLabel || field.label || ""}
                error={error}
              />
            );
          }
          case "textarea":
            return (
              <div>
                {field.label && (
                  <Label className="block text-sm font-medium mb-1">
                    {field.label}
                  </Label>
                )}
                <Textarea
                  {...syncedFieldProps}
                  placeholder={field.placeholder}
                  className="bg-gray-100 border-gray-300"
                />
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
              </div>
            );
          default:
            return (
              <FieldRenderer field={field} control={control} errors={errors} />
            );
        }
      }}
    />
  );
};

const FormWizard: React.FC<FormWizardProps> = ({
  onSuccess,
  onError,
  className,
  currentStepId: externalCurrentStepId,
  onStepChange,
}) => {
  const {
    control,
    errors,
    formData,
    contactData,
    setContactData,
    currentStepNumber,
    totalSteps,
    isSubmitting,
    handleNext,
    handleBack,
    handleFinalSubmit,
    formMethods,
  } = useFormWizard({
    onSuccess,
    onError,
    currentStepId: externalCurrentStepId,
    onStepChange,
  });

  const currentStepConfig = getStepConfig(externalCurrentStepId);

  if (!currentStepConfig) {
    return <div>Invalid step configuration</div>;
  }

  // Render fields for the current step
  const renderFields = () => {
    // Special handling for sub-type step - dynamically get options
    if (externalCurrentStepId === "sub-type") {
      const options = getSubPropertyTypeOptions(
        formData.propertyType as string
      );
      return (
        <FieldRenderer
          field={{
            name: "subPropertyType",
            type: "radio-card",
            options,
            autoAdvance: true,
          }}
          control={control}
          errors={errors}
          onAutoAdvance={handleNext}
        />
      );
    }

    // Special handling for property-size step - dynamic title, no labels
    if (externalCurrentStepId === "property-size") {
      return currentStepConfig.fields
        ?.filter((field) => {
          if (field.conditional) {
            return field.conditional(formData as PropertyFormData);
          }
          return true;
        })
        .map((field) => {
          // Remove label since headline describes the input
          return (
            <FieldRenderer
              key={field.name}
              field={{ ...field, label: undefined }}
              control={control}
              errors={errors}
            />
          );
        });
    }

    // Special handling for additional-size step - dynamic title, no labels
    if (externalCurrentStepId === "additional-size") {
      const filteredFields = currentStepConfig.fields?.filter((field) => {
        if (field.conditional) {
          return field.conditional(formData as PropertyFormData);
        }
        return true;
      });

      if (!filteredFields || filteredFields.length === 0) {
        return null; // No fields to render
      }

      return filteredFields.map((field) => {
        // Remove label since headline describes the input
        return (
          <FieldRenderer
            key={field.name}
            field={{ ...field, label: undefined }}
            control={control}
            errors={errors}
          />
        );
      });
    }

    // Special handling for contact step
    if (externalCurrentStepId === "contact") {
      return renderContactFields();
    }

    // For standard steps, render fields from config
    return currentStepConfig.fields
      ?.filter((field) => {
        // Check conditional visibility
        if (field.conditional) {
          return field.conditional(formData as PropertyFormData);
        }
        return true;
      })
      .map((field) => {
        // Check if field has autoAdvance
        const hasAutoAdvance =
          field.type === "radio-card" && (field as any).autoAdvance;
        return (
          <FieldRenderer
            key={field.name}
            field={field}
            control={control}
            errors={errors}
            onAutoAdvance={hasAutoAdvance ? handleNext : undefined}
          />
        );
      });
  };

  // Render contact form fields
  const renderContactFields = () => {
    return (
      <div className="space-y-4">
        <ContactFieldWrapper
          field={{
            name: "first_name",
            type: "text",
            label: "Vorname",
            placeholder: "Vorname *",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <ContactFieldWrapper
          field={{
            name: "last_name",
            type: "text",
            label: "Nachname",
            placeholder: "Nachname *",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <ContactFieldWrapper
          field={{
            name: "email",
            type: "text",
            label: "E-Mail",
            placeholder: "E-Mail *",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <ContactFieldWrapper
          field={{
            name: "home_phone",
            type: "text",
            label: "Telefonnummer",
            placeholder: "Telefonnummer *",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <ContactFieldWrapper
          field={{
            name: "accept_contact",
            type: "checkbox",
            checkboxLabel: "Ich akzeptiere die Datenschutzbestimmungen *",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <ContactFieldWrapper
          field={{
            name: "newsletter",
            type: "checkbox",
            checkboxLabel: "Newsletter abonnieren",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <ContactFieldWrapper
          field={{
            name: "description",
            type: "textarea",
            label: "Nachricht",
            placeholder: "Nachricht (optional)",
          }}
          control={control}
          errors={errors}
          contactData={contactData}
          setContactData={setContactData}
        />
        <p className="text-xs text-gray-500">* Pflichtfeld</p>
      </div>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <div className={cn("flex flex-col h-full overflow-hidden", className)}>
        <div className="shrink-0 px-3 sm:px-6 pt-4 sm:pt-6">
          <ProgressBar
            currentStep={currentStepNumber}
            totalSteps={totalSteps}
          />
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-6 [&_input]:overflow-visible [&_input]:focus:z-10 [&_*]:overflow-visible">
          <AnimatePresence mode="wait">
            <FormStep
              config={{
                ...currentStepConfig,
                title:
                  getStepTitle(
                    externalCurrentStepId,
                    formData as PropertyFormData
                  ) ||
                  currentStepConfig.title ||
                  "", // Ensure title is always a string
              }}
            >
              {renderFields()}
            </FormStep>
          </AnimatePresence>
        </div>

        <div className="shrink-0 px-3 sm:px-6 py-3 sm:py-4">
          <NavigationButtons
            currentStep={currentStepNumber}
            totalSteps={totalSteps}
            onBack={handleBack}
            onNext={handleNext}
            onSubmit={handleFinalSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default FormWizard;
